/**
 * Appointments Context
 * 
 * Manages appointment/calendar state to prevent unnecessary re-renders
 * across unrelated features when schedule data changes.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppointmentsContext = createContext();

export const AppointmentsProvider = ({ children }) => {
  const { user } = useAuth() || {};
  // Core appointment data
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  
  // Calendar view state
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState('week'); // week (grid), list, day, month
  const [calendarInterval, setCalendarInterval] = useState(15); // 5, 10, 15 minute intervals
  const [selectedProviders, setSelectedProviders] = useState([]);
  // 'include' = show only selected provider calendars (default, matches legacy behavior).
  // 'exclude' = show everyone EXCEPT the selected providers.
  const [selectedProvidersMode, setSelectedProvidersMode] = useState('include');

  // ── Mar 2026 — Saved calendar default ────────────────────────────
  // One-shot fetch on login: pull the user's saved provider filter
  // and seed `selectedProviders` / `selectedProvidersMode` BEFORE the
  // calendar tab mounts so it renders pre-filtered.  Tracks the
  // "loaded" state so AppointmentsTab can show / hide the
  // "Save as default" button based on whether the current filter
  // matches the saved one.
  const [calendarPrefsLoaded, setCalendarPrefsLoaded] = useState(false);
  const [savedCalendarPrefs, setSavedCalendarPrefs] = useState(null);

  useEffect(() => {
    if (!user?.id) {
      setCalendarPrefsLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/users/me/calendar-prefs`);
        if (cancelled) return;
        if (r.data && Array.isArray(r.data.provider_ids) && r.data.provider_ids.length > 0) {
          setSelectedProviders(r.data.provider_ids);
          if (r.data.mode === 'include' || r.data.mode === 'exclude') {
            setSelectedProvidersMode(r.data.mode);
          }
          setSavedCalendarPrefs(r.data);
        }
      } catch (err) {
        // 404 / empty = user has never saved one — silent no-op.
        if (err?.response?.status && err.response.status !== 404) {
          // eslint-disable-next-line no-console
          console.warn('[AppointmentsContext] calendar-prefs fetch failed:', err);
        }
      } finally {
        if (!cancelled) setCalendarPrefsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const saveCalendarDefaultView = useCallback(async () => {
    const body = {
      provider_ids: (selectedProviders || []).filter(id => id && id !== 'all'),
      mode: selectedProvidersMode || 'include',
    };
    const r = await axios.put(`${API}/users/me/calendar-prefs`, body);
    setSavedCalendarPrefs(r.data);
    return r.data;
  }, [selectedProviders, selectedProvidersMode]);

  const clearCalendarDefaultView = useCallback(async () => {
    await axios.delete(`${API}/users/me/calendar-prefs`);
    setSavedCalendarPrefs(null);
  }, []);
  
  // Appointment form
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [appointmentFormData, setAppointmentFormData] = useState({
    client_id: '',
    provider_id: '',
    location_id: '',
    appointment_type: '',
    start_time: '',
    end_time: '',
    notes: '',
    status: 'scheduled',
    is_recurring: false,
    recurrence_pattern: 'weekly',
    recurrence_end_date: '',
    recurrence_days: [],
    pos_code: '',
    google_meet_link: '',
    cpt_code: '',
    group_id: ''
  });
  
  // UI state
  const [appointmentSortAsc, setAppointmentSortAsc] = useState(true); // true = soonest first
  // Feb 2026 — user reported: "i still can't see calendar appointments
  // in the past. we need to be able to see them. even if it is a
  // toggle?"  Default ON so past appointments appear without the
  // biller having to discover the toggle.  Persisted to localStorage
  // so the user's preference survives reloads.
  const [showPastAppointments, setShowPastAppointments] = useState(() => {
    if (typeof window === 'undefined') return true;
    const stored = window.localStorage.getItem('showPastAppointments');
    // Missing key → default true.  Explicit "false" only flips off.
    return stored === null ? true : stored !== 'false';
  });
  // Persist whenever the toggle flips.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('showPastAppointments', String(showPastAppointments));
    } catch {
      // localStorage quota / disabled mode — preference resets next
      // load.  Non-fatal.
    }
  }, [showPastAppointments]);
  const [showCanceledAppointments, setShowCanceledAppointments] = useState(false);
  const [appointmentSearch, setAppointmentSearch] = useState('');
  const [appointmentContextMenu, setAppointmentContextMenu] = useState({ show: false, x: 0, y: 0, appointment: null });
  
  // Drag and drop
  const [draggedAppointment, setDraggedAppointment] = useState(null);
  const [resizingAppointment, setResizingAppointment] = useState(null); // {id, previewEndTime}
  
  // Undo stack for calendar changes
  const [undoStack, setUndoStack] = useState([]);
  
  // Attendance
  const [attendanceAppointment, setAttendanceAppointment] = useState(null);
  const [addAttendeeAppointment, setAddAttendeeAppointment] = useState(null);
  
  // CPT code usage tracking
  const [cptCodeUsage, setCptCodeUsage] = useState({});
  
  // Group appointments
  const [groupAppointments, setGroupAppointments] = useState([]);
  const [showPastGroupAppointments, setShowPastGroupAppointments] = useState(false);
  const [groupNoteAppointment, setGroupNoteAppointment] = useState(null);
  const [addToFutureAppointments, setAddToFutureAppointments] = useState(false);

  // Group Notes dialog state.
  // Jun 17 2026 — migrated out of legacy App.js into this context so
  // the V2 /v2/groups route can be a fully self-contained native route
  // (no leaning on legacy App.js state).  Five fields kept verbatim:
  //   * showGroupNoteDialog       — dialog open/closed
  //   * groupNoteGroup            — the group the note is for
  //   * groupNoteCurriculumSession — selected curriculum session row
  //   * groupNoteExistingNotes    — array of notes already on the
  //                                 group note (edit mode only)
  //   * groupNoteMode             — 'create' | 'edit'
  // Defaults match the legacy App.js declarations exactly so no
  // consumer needs to change behavior — this is a state-relocation
  // refactor, not a semantic change.
  const [showGroupNoteDialog, setShowGroupNoteDialog] = useState(false);
  const [groupNoteGroup, setGroupNoteGroup] = useState(null);
  const [groupNoteCurriculumSession, setGroupNoteCurriculumSession] = useState(null);
  const [groupNoteExistingNotes, setGroupNoteExistingNotes] = useState([]);
  const [groupNoteMode, setGroupNoteMode] = useState('create');

  // Phase-2 stabilization (Feb 2026): wrap `resetAppointmentForm` in
  // `useCallback` with [] — it only calls setters from useState (which
  // are stable), so its identity can be stable.  Without this the
  // function reference flipped every render and broke the `useMemo`
  // on `value` below.
  const resetAppointmentForm = useCallback(() => {
    setAppointmentFormData({
      client_id: '',
      provider_id: '',
      location_id: '',
      appointment_type: '',
      start_time: '',
      end_time: '',
      notes: '',
      status: 'scheduled',
      is_recurring: false,
      recurrence_pattern: 'weekly',
      recurrence_end_date: '',
      recurrence_days: [],
      pos_code: '',
      google_meet_link: '',
      cpt_code: '',
      group_id: ''
    });
    setEditingAppointment(null);
  }, []);

  // Phase-2 stabilization (Feb 2026): memoize the Provider value so
  // consumers don't re-render on every AppointmentsProvider render —
  // only when one of the listed fields actually changes.  Setters from
  // useState are stable and intentionally NOT in the deps array.
  const value = useMemo(() => ({
    // Core data
    appointments,
    setAppointments,
    selectedAppointment,
    setSelectedAppointment,
    
    // Calendar view
    calendarDate,
    setCalendarDate,
    calendarView,
    setCalendarView,
    calendarInterval,
    setCalendarInterval,
    selectedProviders,
    setSelectedProviders,
    selectedProvidersMode,
    setSelectedProvidersMode,
    // Saved calendar default (Mar 2026)
    savedCalendarPrefs,
    calendarPrefsLoaded,
    saveCalendarDefaultView,
    clearCalendarDefaultView,
    
    // Form
    showAppointmentForm,
    setShowAppointmentForm,
    editingAppointment,
    setEditingAppointment,
    appointmentFormData,
    setAppointmentFormData,
    resetAppointmentForm,
    
    // UI state
    appointmentSortAsc,
    setAppointmentSortAsc,
    showPastAppointments,
    setShowPastAppointments,
    showCanceledAppointments,
    setShowCanceledAppointments,
    appointmentSearch,
    setAppointmentSearch,
    appointmentContextMenu,
    setAppointmentContextMenu,
    
    // Drag/drop
    draggedAppointment,
    setDraggedAppointment,
    resizingAppointment,
    setResizingAppointment,
    
    // Undo
    undoStack,
    setUndoStack,
    
    // Attendance
    attendanceAppointment,
    setAttendanceAppointment,
    addAttendeeAppointment,
    setAddAttendeeAppointment,
    
    // CPT tracking
    cptCodeUsage,
    setCptCodeUsage,
    
    // Group appointments
    groupAppointments,
    setGroupAppointments,
    showPastGroupAppointments,
    setShowPastGroupAppointments,
    groupNoteAppointment,
    setGroupNoteAppointment,
    addToFutureAppointments,
    setAddToFutureAppointments,

    // Group Notes dialog (migrated from App.js — Jun 17 2026)
    showGroupNoteDialog,
    setShowGroupNoteDialog,
    groupNoteGroup,
    setGroupNoteGroup,
    groupNoteCurriculumSession,
    setGroupNoteCurriculumSession,
    groupNoteExistingNotes,
    setGroupNoteExistingNotes,
    groupNoteMode,
    setGroupNoteMode,
  }), [
    appointments, selectedAppointment,
    calendarDate, calendarView, calendarInterval,
    selectedProviders, selectedProvidersMode,
    savedCalendarPrefs, calendarPrefsLoaded,
    saveCalendarDefaultView, clearCalendarDefaultView,
    showAppointmentForm, editingAppointment, appointmentFormData,
    appointmentSortAsc, showPastAppointments, showCanceledAppointments,
    appointmentSearch, appointmentContextMenu,
    draggedAppointment, resizingAppointment,
    undoStack,
    attendanceAppointment, addAttendeeAppointment,
    cptCodeUsage,
    groupAppointments, showPastGroupAppointments, groupNoteAppointment, addToFutureAppointments,
    showGroupNoteDialog, groupNoteGroup, groupNoteCurriculumSession,
    groupNoteExistingNotes, groupNoteMode,
    resetAppointmentForm,
  ]);

  return (
    <AppointmentsContext.Provider value={value}>
      {children}
    </AppointmentsContext.Provider>
  );
};

export const useAppointments = () => {
  const context = useContext(AppointmentsContext);
  if (!context) {
    throw new Error('useAppointments must be used within AppointmentsProvider');
  }
  return context;
};

export default AppointmentsContext;
